import React, { useState, Suspense, useCallback } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useMediaQuery, Box, CircularProgress } from "@mui/material";
import { NavBar } from "@hmxlabs/navbar";
import { SchedSelectForm } from "@hmxlabs/sched-select";
import "@hmxlabs/sched-select/styles";
import "./index.css";

// Simple loading component
const LoadingSpinner = ({ message = "Loading..." }: { message?: string }) => (
  <Box className="loading-overlay">
    <Box className="loading-spinner-container">
      <CircularProgress size={40} thickness={4} className="loading-spinner" />
    </Box>
  </Box>
);

// Simple error boundary
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box className="error-boundary">
          <h2>Something went wrong</h2>
          <p style={{ color: 'red', maxWidth: '600px', wordBreak: 'break-word' }}>
            {this.state.error?.message}
          </p>
          <button onClick={() => window.location.reload()}>Refresh Page</button>
        </Box>
      );
    }
    return this.props.children;
  }
}

const App = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = useMediaQuery("(max-width:768px)");

  const handleFormSubmit = useCallback(async (answers: object) => {
    try {
      const response = await fetch('/save-results', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(answers),
      });
      
      if (!response.ok) {
        console.error('Failed to save results:', response.statusText);
      }
    } catch (error) {
      console.error('Error saving results:', error);
    }
  }, []);

  return (
    <BrowserRouter>
      <ErrorBoundary>
        <NavBar
          isMobile={isMobile}
          mobileOpen={mobileOpen}
          onToggleMobileMenu={() => setMobileOpen((prev) => !prev)}
        />
        <Suspense fallback={<LoadingSpinner message="Loading application..." />}>
          <Routes>
            <Route path="/" element={<SchedSelectForm onSubmit={handleFormSubmit} />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default App;
